/**
 * Field Placement editor — Phase 1A of #3.
 * Per-tenant override of which tab + position each picklist-driven field
 * sits on within the New Booking form. Persists to lbs_picklist_field_layout.
 *
 * Booking-form layout-driven rendering arrives in Phase 1B; until then this
 * UI captures intent without changing runtime behaviour.
 */
import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Layout, ChevronUp, ChevronDown } from "lucide-react";
import { listPicklistFieldLayout, upsertPicklistFieldLayout } from "@/lib/api";

// Tabs available in the New Booking form
const TABS = [
  { value: "schedule",   label: "Schedule" },
  { value: "vehicle",    label: "Vehicle" },
  { value: "driver",     label: "Driver" },
  { value: "goods",      label: "Goods" },
  { value: "restraint",  label: "Restraint" },
  { value: "qa",         label: "QA" },
  { value: "compliance", label: "Compliance" },
  { value: "docs",       label: "Docs" },
];

// Booker-facing picklist categories only.
// • PPE → per-site Site Info accordion (`ppe_checklist`) — configured at
//   /admin/sites/:id/info-sections, surfaced on the public site-info page.
// • temp_set → FLD-only entry on the QA tab (dock-time, not pre-filled).
// • issue_type → NCR raise dialog, not a static booking-form field.
const CATEGORIES = [
  { category: "vehicle_type",     label: "Vehicle type" },
  { category: "goods_category",   label: "Goods category" },
  { category: "pallet_type",      label: "Pallet brand" },
  { category: "load_rule",        label: "Load rule" },
  { category: "restraint_method", label: "Restraint method" },
  { category: "cold_chain",       label: "Cold chain" },
];

// Display order for tab grouping in the placement editor.
const TAB_ORDER = ["schedule", "vehicle", "driver", "goods", "restraint", "qa", "compliance", "docs"];

export default function FieldPlacementEditor({ clientId }) {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!clientId || clientId === "__global__") {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data } = await listPicklistFieldLayout(clientId);
      if (cancelled) return;
      setRows(data || []);
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  // Resolve effective placement per category (tenant override > global default).
  // Then sort by (tab order, position, label) and group by tab for display.
  const resolved = useMemo(() => {
    const list = CATEGORIES.map((c) => {
      const tenantRow = rows.find((r) => r.category === c.category && r.client_id === clientId);
      const globalRow = rows.find((r) => r.category === c.category && r.client_id === null);
      const effective = tenantRow || globalRow;
      return {
        ...c,
        target_tab: effective?.target_tab || "vehicle",
        target_position: effective?.target_position ?? 0,
        isOverride: !!tenantRow,
      };
    });
    list.sort((a, b) => {
      const ta = TAB_ORDER.indexOf(a.target_tab);
      const tb = TAB_ORDER.indexOf(b.target_tab);
      if (ta !== tb) return ta - tb;
      if (a.target_position !== b.target_position) return a.target_position - b.target_position;
      return a.label.localeCompare(b.label);
    });
    return list;
  }, [rows, clientId]);

  // Group resolved rows by tab so the table renders with a header per tab.
  const grouped = useMemo(() => {
    const m = new Map();
    resolved.forEach((r) => {
      if (!m.has(r.target_tab)) m.set(r.target_tab, []);
      m.get(r.target_tab).push(r);
    });
    return Array.from(m.entries()); // [ [tab, rows[]], ... ]
  }, [resolved]);

  const save = async (category, target_tab, target_position) => {
    setSaving(true);
    const { error } = await upsertPicklistFieldLayout({
      client_id: clientId,
      category,
      target_tab,
      target_position,
    });
    setSaving(false);
    if (error) {
      toast.error(error.message || "Could not save placement");
      return;
    }
    // Refresh local state
    const { data } = await listPicklistFieldLayout(clientId);
    setRows(data || []);
    toast.success("Placement saved");
  };

  if (!clientId || clientId === "__global__") {
    return (
      <Card data-testid="field-placement-disabled">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Layout className="h-4 w-4" /> Field placement
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              Pick a specific tenant above to configure field placement. Global
              defaults apply if no tenant override is set.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="field-placement-editor">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <Layout className="h-4 w-4" /> Field placement
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">Field</th>
              <th className="text-left px-4 py-2">Tab</th>
              <th className="text-left px-4 py-2">Position</th>
              <th className="text-left px-4 py-2">Source</th>
            </tr>
          </thead>
          <tbody>
            {grouped.map(([tabValue, tabRows]) => {
              const tabLabel = TABS.find((t) => t.value === tabValue)?.label || tabValue;
              return (
                <FieldPlacementTabGroup
                  key={tabValue}
                  tabLabel={tabLabel}
                  tabRows={tabRows}
                  saving={saving}
                  tabs={TABS}
                  onSave={save}
                />
              );
            })}
          </tbody>
        </table>
        <div className="p-3 border-t bg-slate-50 text-[12px] text-slate-600">
          Tip: rows are grouped by the tab they&apos;ll appear on in the
          New Booking form. PPE is configured per site (Site Info →{" "}
          <em>PPE checklist</em>), not here.
        </div>
      </CardContent>
    </Card>
  );
}

function FieldPlacementTabGroup({ tabLabel, tabRows, saving, tabs, onSave }) {
  return (
    <>
      <tr className="bg-slate-50/70">
        <td colSpan={4} className="px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {tabLabel} tab
        </td>
      </tr>
      {tabRows.map((r) => (
        <tr key={r.category} className="border-t" data-testid={`placement-row-${r.category}`}>
          <td className="px-4 py-2 font-medium">{r.label}</td>
          <td className="px-4 py-2">
            <Select
              value={r.target_tab}
              onValueChange={(v) => onSave(r.category, v, r.target_position)}
            >
              <SelectTrigger className="w-40" data-testid={`tab-select-${r.category}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {tabs.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </td>
          <td className="px-4 py-2">
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSave(r.category, r.target_tab, Math.max(0, r.target_position - 1))}
                disabled={saving || r.target_position === 0}
                data-testid={`pos-up-${r.category}`}
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </Button>
              <span className="font-mono text-sm w-6 text-center">{r.target_position}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onSave(r.category, r.target_tab, r.target_position + 1)}
                disabled={saving}
                data-testid={`pos-down-${r.category}`}
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </Button>
            </div>
          </td>
          <td className="px-4 py-2">
            {r.isOverride ? (
              <span className="text-xs text-green-700 font-medium">tenant override</span>
            ) : (
              <span className="text-xs text-slate-500">global default</span>
            )}
          </td>
        </tr>
      ))}
    </>
  );
}
